import { valOrThrow } from '@nfctron/nexus-core';

import { type StateCreator } from 'zustand';

type CartViewId = string | number;

interface CartViewConfig<TCartViewId extends CartViewId, TGlobalState> {
	viewId: TCartViewId;
	getIsEnabled: boolean | ((state: TGlobalState & CartMultiViewSlice<TCartViewId, TGlobalState>) => boolean);
	getIsVisible: boolean | ((state: TGlobalState & CartMultiViewSlice<TCartViewId, TGlobalState>) => boolean);
	getIsSelectable: boolean | ((state: TGlobalState & CartMultiViewSlice<TCartViewId, TGlobalState>) => boolean);
}

interface CartViewState {
	isEnabled: boolean;
	isVisible: boolean;
	isSelectable: boolean;
	isFirst: boolean;
	isLast: boolean;
}

export interface CartMultiViewSlice<TCartViewId extends CartViewId, TGlobalState> {
	currentCartView: TCartViewId;
	cartViewsConfig: Array<CartViewConfig<TCartViewId, TGlobalState>>;

	setCartView: (cartView: TCartViewId) => void;
	getCartViewConfig: (cartView: TCartViewId) => CartViewConfig<TCartViewId, TGlobalState>;
	getCartViewAtPos: (pos: number) => TCartViewId | undefined;
	canChangeCartViewTo: (cartView: TCartViewId) => boolean;

	getCartViewEvaluated: (params: { view: TCartViewId }) => CartViewState;
	getCartViewPos: (params: { cartView: TCartViewId; humanReadable?: boolean; onlyVisible?: boolean }) => [current: number, total: number];
	getNextCartView: (params: { prop: keyof CartViewState; view?: TCartViewId }) => TCartViewId;
	getPrevCartView: (params: { prop: keyof CartViewState; view?: TCartViewId }) => TCartViewId;
}

interface CartMultiViewConfig<TCartViewId extends CartViewId, TGlobalState> {
	restoreScroll?: boolean;
	onCartViewChange?: (cartView: CartViewId) => void;
	cartViewsConfig: Array<CartViewConfig<TCartViewId, TGlobalState>>;
}

const evaluateCondition = <TCartViewId extends CartViewId, TGlobalState>(
	condition: boolean | ((state: TGlobalState) => boolean),
	state: TGlobalState,
): boolean => {
	if (typeof condition === 'boolean') return condition;
	return condition?.(state) ?? false;
};

export const createCartMultiViewSlice =
	<TCartViewId extends CartViewId, TGlobalState>(
		initialCartView: TCartViewId,
		config: CartMultiViewConfig<TCartViewId, TGlobalState>,
	): StateCreator<CartMultiViewSlice<TCartViewId, TGlobalState>, [['zustand/devtools', never]], [], CartMultiViewSlice<TCartViewId, TGlobalState>> =>
	(set, get, api) => {
		return {
			currentCartView: initialCartView,
			cartViewsConfig: config.cartViewsConfig,
			getCartViewConfig(view) {
				return valOrThrow(
					get().cartViewsConfig.find((v) => v.viewId === view),
					`Cart view ${view} not found`,
				);
			},
			/** sets the active cart view and handles side effects */
			setCartView(cartView) {
				/** get state for target cart view */
				const cartViewState = get().getCartViewEvaluated({ view: cartView });
				if (!cartViewState.isEnabled) return;

				/** update store and handle configured side effects */
				set({ currentCartView: cartView }, undefined, 'setCartView');
				if (config.restoreScroll) window.scrollTo(0, 0);
				config.onCartViewChange?.(cartView);
			},
			/** determines if transition to target cart view is possible */
			canChangeCartViewTo(to) {
				const { getCartViewPos, currentCartView } = get();

				/** find indices of current and target views */
				const fromIndex = getCartViewPos({ cartView: currentCartView, humanReadable: false, onlyVisible: true })[0];
				const toIndex = getCartViewPos({ cartView: to, humanReadable: false, onlyVisible: true })[0];

				/** validate view existence */
				if (fromIndex === -1 || toIndex === -1) return false;

				/** check if views are adjacent and target is enabled */
				const isAdjacent = Math.abs(toIndex - fromIndex) === 1;
				const isEnabled = get().getCartViewEvaluated({ view: to }).isEnabled;

				return isAdjacent && isEnabled;
			},
			/** evaluates the full state of a cart view based on its configuration */
			getCartViewEvaluated({ view }) {
				const state = get() as TGlobalState & CartMultiViewSlice<TCartViewId, TGlobalState>;
				const viewConfig = get().getCartViewConfig(view);

				/** evaluate core view states */
				const isEnabled = evaluateCondition(viewConfig.getIsEnabled, state);
				const isVisible = evaluateCondition(viewConfig.getIsVisible, state);
				/** view is selectable only if enabled and meets selectable condition */
				const isSelectable = isEnabled && evaluateCondition(viewConfig.getIsSelectable, state);

				/** get list of currently visible views for position calculation */
				const visibleViews = get().cartViewsConfig.filter((v) => evaluateCondition(v.getIsVisible, state));
				/** find position of current view among visible views */
				const currentPos = visibleViews.findIndex((v) => v.viewId === view);

				return {
					isEnabled,
					isVisible,
					isSelectable,
					/** first/last flags help with navigation boundaries */
					isFirst: currentPos === 0,
					isLast: currentPos === visibleViews.length - 1,
				};
			},
			/** retrieves cart view id at specified position in visible views array */
			getCartViewAtPos(pos) {
				const state = get() as TGlobalState & CartMultiViewSlice<TCartViewId, TGlobalState>;

				/** filter config to get only visible views */
				const visibleViews = get().cartViewsConfig.filter((v) => evaluateCondition(v.getIsVisible, state));

				/** return view id at position or undefined if position is out of bounds */
				return visibleViews[pos]?.viewId;
			},
			/** gets the position of a view in the visible views array */
			getCartViewPos(params) {
				const { cartView, humanReadable, onlyVisible } = params;
				const state = get() as TGlobalState & CartMultiViewSlice<TCartViewId, TGlobalState>;

				/** filter to get only visible views */
				const visibleViews = onlyVisible ? get().cartViewsConfig.filter((v) => evaluateCondition(v.getIsVisible, state)) : get().cartViewsConfig;

				/** find index of target view */
				const index = visibleViews.findIndex((v) => v.viewId === cartView);

				/** add 1 to index for human-readable position (1-based instead of 0-based) */
				const offset = humanReadable ? 1 : 0;

				return [index + offset, visibleViews.length + offset];
			},
			/** finds next cart view matching specified property condition */
			getNextCartView({ prop, view = get().currentCartView }) {
				const { cartViewsConfig } = get();
				const index = cartViewsConfig.findIndex((v) => v.viewId === view);

				/** search in subsequent views */
				const nextViews = cartViewsConfig.slice(index + 1);
				const matchingView = nextViews.find((v) => get().getCartViewEvaluated({ view: v.viewId })[prop]);

				return matchingView?.viewId ?? view;
			},
			/** finds previous cart view matching specified property condition */
			getPrevCartView({ prop, view = get().currentCartView }) {
				const { cartViewsConfig } = get();
				const index = cartViewsConfig.findIndex((v) => v.viewId === view);

				/** search in previous views (in reverse order) */
				const previousViews = cartViewsConfig.slice(0, index).reverse();
				const matchingView = previousViews.find((v) => get().getCartViewEvaluated({ view: v.viewId })[prop]);

				return matchingView?.viewId ?? view;
			},
		};
	};
